import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import AddressAutocomplete from '../components/AddressAutocomplete';
import FloatingTacoWidget from '../components/FloatingTacoWidget';
import {
  ArrowLeft,
  Calendar as CalendarIcon,
  Loader2,
  Save,
  Repeat,
  AlertCircle,
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const EditBookingPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [booking, setBooking] = useState(null);
  const [showSeriesDialog, setShowSeriesDialog] = useState(false);
  const [editScope, setEditScope] = useState('single'); // 'single' or 'series'
  
  const [formData, setFormData] = useState({
    service_address: '',
    scheduled_date: new Date(),
    time_window_start: '09:00',
    time_window_end: '12:00',
    job_details: '',
    access_notes: '',
    estimated_duration_minutes: 120,
    price: '',
    assigned_staff_id: '',
  });
  
  const [staff, setStaff] = useState([]);

  useEffect(() => {
    fetchBooking();
    fetchStaff();
  }, [id]);

  const fetchBooking = async () => {
    try {
      const response = await api().get(`/bookings/${id}`);
      const data = response.data;
      setBooking(data);
      setFormData({
        service_address: data.service_address || '',
        scheduled_date: new Date(data.scheduled_date),
        time_window_start: data.time_window_start || '09:00',
        time_window_end: data.time_window_end || '12:00',
        job_details: data.job_details || '',
        access_notes: data.access_notes || '',
        estimated_duration_minutes: data.estimated_duration_minutes || 120,
        price: data.price?.toString() || '',
        assigned_staff_id: data.assigned_staff_id || '',
      });
    } catch (error) {
      console.error('Failed to fetch booking:', error);
      toast.error('Failed to load booking');
      navigate('/bookings');
    } finally {
      setLoading(false);
    }
  };

  const fetchStaff = async () => {
    try {
      const response = await api().get('/admin/users');
      const staffUsers = response.data.users.filter(u => u.is_active);
      setStaff(staffUsers);
    } catch (error) {
      console.error('Failed to fetch staff:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // If recurring booking, show dialog to choose scope
    if (booking?.is_recurring && booking?.series_id && !showSeriesDialog) {
      setShowSeriesDialog(true);
      return;
    }
    
    await saveBooking();
  };

  const saveBooking = async () => {
    setSaving(true);
    
    try {
      const payload = {
        service_address: formData.service_address,
        scheduled_date: formData.scheduled_date.toISOString(),
        time_window_start: formData.time_window_start,
        time_window_end: formData.time_window_end,
        job_details: formData.job_details || null,
        access_notes: formData.access_notes || null,
        estimated_duration_minutes: parseInt(formData.estimated_duration_minutes) || null,
        price: formData.price ? parseFloat(formData.price) : null,
        assigned_staff_id: formData.assigned_staff_id || null,
      };
      
      if (booking?.is_recurring && booking?.series_id && editScope === 'series') {
        // Edit entire series (future bookings)
        await api().patch(`/bookings/series/${booking.series_id}`, payload, {
          params: { future_only: true }
        });
        toast.success('All future bookings in series updated!');
      } else {
        // Edit single booking
        await api().patch(`/bookings/${id}/single`, payload);
        toast.success('Booking updated successfully!');
      }
      
      navigate(`/bookings/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update booking');
    } finally {
      setSaving(false);
      setShowSeriesDialog(false);
    }
  };

  const timeSlots = [
    '06:00', '06:30', '07:00', '07:30', '08:00', '08:30', '09:00', '09:30',
    '10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
    '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
    '18:00'
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (!booking) {
    return null;
  }

  return (
    <div className="space-y-6 pb-20 md:pb-0 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`/bookings/${id}`)}
          data-testid="back-button"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900">Edit Booking</h1>
          <div className="flex items-center gap-2 text-zinc-500 text-sm mt-1">
            <span>{booking.client?.name || 'Unknown Client'}</span>
            {booking.is_recurring && (
              <span className="flex items-center gap-1 text-blue-600">
                <Repeat className="h-3 w-3" />
                Recurring ({booking.recurrence_pattern})
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Recurring Notice */}
      {booking.is_recurring && booking.series_id && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">This is a recurring booking</p>
              <p className="text-sm text-blue-700">
                When you save, you'll be asked whether to update just this instance or all future bookings in the series.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Location */}
        <Card className="border-zinc-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Location</CardTitle>
          </CardHeader>
          <CardContent>
            <div>
              <Label htmlFor="service_address">Service Address</Label>
              <div className="mt-2">
                <AddressAutocomplete
                  value={formData.service_address}
                  onChange={(address) => setFormData({ ...formData, service_address: address })}
                  placeholder="Search or enter address..."
                  data-testid="service-address-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Schedule */}
        <Card className="border-zinc-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Schedule</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal mt-2',
                      !formData.scheduled_date && 'text-muted-foreground'
                    )}
                    data-testid="date-picker-trigger"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.scheduled_date ? format(formData.scheduled_date, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={formData.scheduled_date}
                    onSelect={(date) => date && setFormData({ ...formData, scheduled_date: date })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Time</Label>
                <Select
                  value={formData.time_window_start}
                  onValueChange={(value) => setFormData({ ...formData, time_window_start: value })}
                >
                  <SelectTrigger className="mt-2" data-testid="start-time-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((time) => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>End Time</Label>
                <Select
                  value={formData.time_window_end}
                  onValueChange={(value) => setFormData({ ...formData, time_window_end: value })}
                >
                  <SelectTrigger className="mt-2" data-testid="end-time-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((time) => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Assignment */}
        <Card className="border-zinc-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Assignment</CardTitle>
          </CardHeader>
          <CardContent>
            <div>
              <Label>Assign Staff</Label>
              <Select
                value={formData.assigned_staff_id || 'unassigned'}
                onValueChange={(value) => setFormData({ ...formData, assigned_staff_id: value === 'unassigned' ? '' : value })}
              >
                <SelectTrigger className="mt-2" data-testid="staff-select">
                  <SelectValue placeholder="Select staff member" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {staff.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.full_name} ({member.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Job Details */}
        <Card className="border-zinc-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Job Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="job_details">Job Description</Label>
              <Textarea
                id="job_details"
                value={formData.job_details}
                onChange={(e) => setFormData({ ...formData, job_details: e.target.value })}
                placeholder="Describe the cleaning requirements..."
                className="mt-2"
                data-testid="job-details-input"
              />
            </div>

            <div>
              <Label htmlFor="access_notes">Access Instructions</Label>
              <Textarea
                id="access_notes"
                value={formData.access_notes}
                onChange={(e) => setFormData({ ...formData, access_notes: e.target.value })}
                placeholder="How to access the property..."
                className="mt-2"
                data-testid="access-notes-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="duration">Duration (minutes)</Label>
                <Input
                  id="duration"
                  type="number"
                  value={formData.estimated_duration_minutes}
                  onChange={(e) => setFormData({ ...formData, estimated_duration_minutes: e.target.value })}
                  className="mt-2"
                  data-testid="duration-input"
                />
              </div>
              <div>
                <Label htmlFor="price">Price</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="$0.00"
                  className="mt-2"
                  data-testid="price-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex gap-4">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() => navigate(`/bookings/${id}`)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="flex-1 bg-zinc-900 hover:bg-zinc-800"
            disabled={saving}
            data-testid="save-booking-btn"
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Edit Scope Dialog (for recurring bookings) */}
      <Dialog open={showSeriesDialog} onOpenChange={setShowSeriesDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Repeat className="h-5 w-5 text-blue-600" />
              Edit Recurring Booking
            </DialogTitle>
            <DialogDescription>
              This booking is part of a recurring series. How would you like to apply your changes?
            </DialogDescription>
          </DialogHeader>
          
          <RadioGroup value={editScope} onValueChange={setEditScope} className="py-4">
            <div className="flex items-start space-x-3 p-3 rounded-lg border border-zinc-200 hover:bg-zinc-50 cursor-pointer"
                 onClick={() => setEditScope('single')}>
              <RadioGroupItem value="single" id="single" className="mt-1" />
              <div>
                <Label htmlFor="single" className="font-medium cursor-pointer">This booking only</Label>
                <p className="text-sm text-zinc-500">
                  Only update this specific instance. Other bookings in the series will remain unchanged.
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3 p-3 rounded-lg border border-zinc-200 hover:bg-zinc-50 cursor-pointer mt-3"
                 onClick={() => setEditScope('series')}>
              <RadioGroupItem value="series" id="series" className="mt-1" />
              <div>
                <Label htmlFor="series" className="font-medium cursor-pointer">All future bookings</Label>
                <p className="text-sm text-zinc-500">
                  Update this and all future bookings in the series. Past and completed bookings won't change.
                </p>
              </div>
            </div>
          </RadioGroup>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSeriesDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={saveBooking}
              disabled={saving}
              data-testid="confirm-edit-scope-btn"
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Apply Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Floating Taco Assistant */}
      <FloatingTacoWidget 
        context="booking_creation"
        contextData={{
          client: booking ? {
            name: booking.client_name,
            service_address: booking.service_address
          } : null,
          package: formData.package_name
        }}
      />
    </div>
  );
};

export default EditBookingPage;
